import { CopyIcon, EditIcon, ReplaceIcon, UsersIcon } from '@synergy/theme-next';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolConfirmationModal, PoolFormattedMessage, useIsMobile } from '@synergy/components-next';
import { PoolInstance, isInstance } from '@synergy/config';
import { ReactElement, useState } from 'react';
import { Stack, styled } from '@mui/material';
import { useParams } from 'react-router';

import { WishListCopyModal } from './modals/wish-list-copy-modal';
import { WishListId } from '../../models/wish-list-list-item';
import { WishListShareModal } from './modals/wish-list-share-modal';
import { copyWishListToCart } from '../../store/whish-list-actions';
import { shoppingCartMinimalInfoSelector } from '../../store/shopping-cart-selectors';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { wishListsSelector } from '../../store/whish-list-selectors';

const StyledStack = styled(Stack)(({ theme }) => ({
    flexDirection: 'row-reverse',
    margin: theme.spacing(4, 1, 2, 0),
    [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(2),
    },
}));

interface WishListTopActionBarProps {
    onEditWishList: (id: WishListId) => void;
    canEdit: boolean;
}

export const WishListTopActionBar = (props: WishListTopActionBarProps): ReactElement => {
    const [showReplaceConfirmation, setShowReplaceConfirmation] = useState(false);
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [showCopyDialog, setShowCopyDialog] = useState(false);
    const dispatch = useAppDispatch();
    const wishLists = useAppSelector(wishListsSelector);
    const cartInfo = useAppSelector(shoppingCartMinimalInfoSelector);
    const isMobile = useIsMobile();
    const { id: paramId = '' } = useParams<{ id?: string }>();
    const wishListId = +paramId || 'default';
    const wishList = wishLists.find(list => list.id === wishListId);

    const canShare = typeof wishListId === 'number' && !isMobile && !!wishList?.isOwner;
    const canEdit = props.canEdit && typeof wishListId === 'number' && !isMobile;

    const handleCopyToCart = (): void => {
        setShowReplaceConfirmation(true);
    };

    const handleCopyToCartConfirm = (): void => {
        dispatch(copyWishListToCart(wishListId));
        setShowReplaceConfirmation(false);
    };

    return (
        <StyledStack>
            {canEdit && (
                <PoolButton
                    variant="text"
                    color="dark"
                    startIcon={<EditIcon />}
                    onClick={() => props.onEditWishList(wishListId)}
                    fullWidth={isMobile}
                    data-testid="replace-cart-button"
                >
                    <FormattedMessage id="app.wishList.editList" />
                </PoolButton>
            )}
            {canShare && (
                <PoolButton
                    variant="text"
                    color="dark"
                    startIcon={<UsersIcon />}
                    onClick={() => setShowShareDialog(true)}
                    fullWidth={isMobile}
                    data-testid="replace-cart-button"
                >
                    <FormattedMessage id="app.wishList.share" />
                </PoolButton>
            )}
            {isInstance(PoolInstance.CH) && (
                <PoolButton
                    variant="text"
                    color="dark"
                    startIcon={<CopyIcon />}
                    onClick={() => setShowCopyDialog(true)}
                    fullWidth={isMobile}
                    data-testid="replace-cart-button"
                >
                    <FormattedMessage id="app.wishList.copy" />
                </PoolButton>
            )}
            <PoolButton
                variant="text"
                color="dark"
                startIcon={<ReplaceIcon />}
                onClick={handleCopyToCart}
                fullWidth={isMobile}
                data-testid="replace-cart-button"
            >
                <FormattedMessage id="app.wishList.replaceCart" />
            </PoolButton>

            <PoolConfirmationModal
                open={showReplaceConfirmation}
                onCancel={() => setShowReplaceConfirmation(false)}
                onConfirm={handleCopyToCartConfirm}
                isSuccessDialog={true}
                action={
                    isMobile ? (
                        <FormattedMessage id="app.wishList.replaceCart.action.mobile" />
                    ) : (
                        <FormattedMessage id="app.wishList.replaceCart.action" />
                    )
                }
                title={<FormattedMessage id="app.wishList.replaceCart" />}
            >
                <PoolFormattedMessage
                    id={
                        cartInfo.itemCount > 0
                            ? 'app.wishList.replaceCart.content'
                            : 'app.wishList.replaceCart.content.zero'
                    }
                    values={{
                        name: wishLists.find(list => list.id === wishListId)?.name ?? '',
                        count: cartInfo.itemCount,
                    }}
                />
            </PoolConfirmationModal>

            <WishListShareModal open={showShareDialog} onClose={() => setShowShareDialog(false)} />
            <WishListCopyModal open={showCopyDialog} onClose={() => setShowCopyDialog(false)} />
        </StyledStack>
    );
};
